import React, { useContext, useEffect, useState } from "react";

import { Theme, useTheme } from "@mui/material/styles";

import {
  Divider,
  Grid,
  Typography,
} from "@mui/material";

import {
  PackableAppContext,
  SnackbarComponentVariant,
  logDebug,
  mergeProperties,
} from "packable-components";
import {
  OrderStatusSummary,
  OrderStatusSummaryEntity,
  OrderStatusSummaryComponentProps,
  ShipmentType,
  ShippingStatus,
  ShippingStatusCount,
  StorageStatus,
  StorageStatusCount,
} from "./Order.types";

const useStyles = (theme: Theme) => ({
  PackableOrderStatusSummaryComponentPageBody: {
    p: 2, 
    width: "100%", 
    display: "flex", 
    flexDirection: "column", 
    justifyContent: "center", 
  }, 
  PackableOrderStatusSummaryComponentTitleContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "space-between",
    alignItems: "center",
    alignContent: "center",
    pt: 1,
  },
  PackableOrderStatusSummaryComponentTitle: {
    muiVariant: "h6",
  },
  PackableSystmeInfoAttributesContainer: {},
  PackableSystmeInfoAttributesDetailsRow: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignContent: "center",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: 0.5,
  },
  PackableSystmeInfoAttributesDetailsLabel: {
    display: "flex",
    flexDirection: "row",
    width: "220px",
    justifyContent: "flex-end",
    pr: 3,
  },
  PackableOrderStatusSummaryComponentAttributesDetailsLabelText: {
    muiVariant: "body2",
  },
  PackableOrderStatusSummaryComponentAttributesHighlightedDetailsLabelText: {
    textDecoration: "underline", 
    muiVariant: "body2",
    fontWeight: "700",
  },
  PackableOrderStatusSummaryComponentAttributesDetailsValueText: {
    muiVariant: "body2",
  },
  PackableOrderStatusSummaryComponentAttributesHighlightedDetailsValueText: {
    textDecoration: "underline", 
    muiVariant: "body2",
    fontWeight: "700",
  },
});

export const OrderStatusSummaryComponent: React.FC<
  OrderStatusSummaryComponentProps
> = (props): React.JSX.Element => {
  const { displayMessage, setDisplaySpinner, styles } = props;

  logDebug("OrderStatusSummaryComponent PROPS");
  logDebug(props);

  const theme = useTheme();
  const defaultStyles = useStyles(theme);
  const effectiveStyles =
    styles === undefined
      ? defaultStyles
      : mergeProperties(defaultStyles, styles);

  logDebug("OrderStatusSummaryComponent Effective Styles:");
  logDebug(effectiveStyles);

  const { httpService } = useContext(PackableAppContext);

  const [orderStatusSummary, setOrderStatusSummary] =
    useState<OrderStatusSummary>(new OrderStatusSummaryEntity());

  ///////////////////////
  //// API callbacks ////
  ///////////////////////
  const onOrderStatusSummaryGetSuccess = (data: any) => {
    setOrderStatusSummary(data.data);
  };
  const onOrderStatusSummaryGetError = (message: string, data: any) => {
    logDebug("Failed to get application info");
    logDebug(message);
    displayMessage(SnackbarComponentVariant.error, message);
  };

  const getOrderStatusSummary = () => {
    let url = "/order/status";
    httpService.get(
      url,
      onOrderStatusSummaryGetSuccess,
      onOrderStatusSummaryGetError
    );
  };

  const getShippingStatusCount = (
    shipmentType: ShipmentType,
    shippingStatus: ShippingStatus
  ): ShippingStatusCount | undefined => {
    if (orderStatusSummary) {
      const statusCount = orderStatusSummary.shippingStatusCounts.find(
        (stc) => stc.type === shipmentType && stc.status === shippingStatus
      );
      if (statusCount) {
        return statusCount;
      }
    }
    return { type: shipmentType, status: shippingStatus, count: 0 };
  };

  const isShippingStatusHighlighted = (status: ShippingStatus): boolean => {
    return (
      status === ShippingStatus.REQUESTED ||
      status === ShippingStatus.CANCEL_REQUESTED ||
      status === ShippingStatus.PICKUP_REQUESTED
    );
  };

  const isStorageStatusHighlighted = (status: StorageStatus): boolean => {
    return status === StorageStatus.EXPIRED;
  };

  const getStorageStatusCount = (
    storageStatus: StorageStatus
  ): StorageStatusCount | undefined => {
    if (orderStatusSummary) {
      const statusCount = orderStatusSummary.storageStatusCounts.find(
        (stc) => stc.status === storageStatus
      );
      if (statusCount) {
        return statusCount;
      }
    }
    return { status: storageStatus, count: 0 };
  };

  useEffect(() => {
    getOrderStatusSummary();
  }, [httpService]);

  const renderShippingStatusCount = (
    statusCount: ShippingStatusCount | undefined
  ) => {
    if (statusCount === undefined) {
      return <></>;
    }
    return (
      <Grid
        key={statusCount.status}
        sx={effectiveStyles.PackableSystmeInfoAttributesDetailsRow}
      >
        <Grid sx={effectiveStyles.PackableSystmeInfoAttributesDetailsLabel}>
          <Typography
            className={
              isShippingStatusHighlighted(statusCount.status)
                ? "PackableOrderStatusSummaryComponentAttributesHighlightedDetailsLabelText"
                : "PackableOrderStatusSummaryComponentAttributesDetailsLabelText"
            }
            sx={
              isShippingStatusHighlighted(statusCount.status)
                ? effectiveStyles.PackableOrderStatusSummaryComponentAttributesHighlightedDetailsLabelText
                : effectiveStyles.PackableOrderStatusSummaryComponentAttributesDetailsLabelText
            }
            variant={
              isShippingStatusHighlighted(statusCount.status)
                ? effectiveStyles
                    .PackableOrderStatusSummaryComponentAttributesHighlightedDetailsLabelText
                    .muiVariant
                : effectiveStyles
                    .PackableOrderStatusSummaryComponentAttributesDetailsLabelText
                    .muiVariant
            }
          >
            {statusCount.status}
          </Typography>
        </Grid>
        <Grid>
          <Typography
            className={
              isShippingStatusHighlighted(statusCount.status)
                ? "PackableOrderStatusSummaryComponentAttributesHighlightedDetailsValueText"
                : "PackableOrderStatusSummaryComponentAttributesDetailsValueText"
            }
            sx={
              isShippingStatusHighlighted(statusCount.status)
                ? effectiveStyles.PackableOrderStatusSummaryComponentAttributesHighlightedDetailsValueText
                : effectiveStyles.PackableOrderStatusSummaryComponentAttributesDetailsValueText
            }
            variant={
              isShippingStatusHighlighted(statusCount.status)
                ? effectiveStyles
                    .PackableOrderStatusSummaryComponentAttributesHighlightedDetailsValueText
                    .muiVariant
                : effectiveStyles
                    .PackableOrderStatusSummaryComponentAttributesDetailsValueText
                    .muiVariant
            }
          >
            {statusCount.count}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  const renderStorageStatusCount = (
    statusCount: StorageStatusCount | undefined
  ) => {
    if (statusCount === undefined) {
      return <></>;
    }
    return (
      <Grid
        key={statusCount.status}
        sx={effectiveStyles.PackableSystmeInfoAttributesDetailsRow}
      >
        <Grid sx={effectiveStyles.PackableSystmeInfoAttributesDetailsLabel}>
          <Typography
            className={
              isStorageStatusHighlighted(statusCount.status)
                ? "PackableOrderStatusSummaryComponentAttributesHighlightedDetailsLabelText"
                : "PackableOrderStatusSummaryComponentAttributesDetailsLabelText"
            }
            sx={
              isStorageStatusHighlighted(statusCount.status)
                ? effectiveStyles.PackableOrderStatusSummaryComponentAttributesHighlightedDetailsLabelText
                : effectiveStyles.PackableOrderStatusSummaryComponentAttributesDetailsLabelText
            }
            variant={
              isStorageStatusHighlighted(statusCount.status)
                ? effectiveStyles
                    .PackableOrderStatusSummaryComponentAttributesHighlightedDetailsLabelText
                    .muiVariant
                : effectiveStyles
                    .PackableOrderStatusSummaryComponentAttributesDetailsLabelText
                    .muiVariant
            }
          >
            {statusCount.status}
          </Typography>
        </Grid>
        <Grid>
          <Typography
            className={
              isStorageStatusHighlighted(statusCount.status)
                ? "PackableOrderStatusSummaryComponentAttributesHighlightedDetailsValueText"
                : "PackableOrderStatusSummaryComponentAttributesDetailsValueText"
            }
            sx={
              isStorageStatusHighlighted(statusCount.status)
                ? effectiveStyles.PackableOrderStatusSummaryComponentAttributesHighlightedDetailsValueText
                : effectiveStyles.PackableOrderStatusSummaryComponentAttributesDetailsValueText
            }
            variant={
              isStorageStatusHighlighted(statusCount.status)
                ? effectiveStyles
                    .PackableOrderStatusSummaryComponentAttributesHighlightedDetailsValueText
                    .muiVariant
                : effectiveStyles
                    .PackableOrderStatusSummaryComponentAttributesDetailsValueText
                    .muiVariant
            }
          >
            {statusCount.count}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  return (
    <div>
      <Grid
        className="PackableOrderStatusSummaryComponentPageBody"
        sx={effectiveStyles.PackableOrderStatusSummaryComponentPageBody}
      >
          <Grid
            className="PackableOrderStatusSummaryComponentTitleContainer"
            sx={
              effectiveStyles.PackableOrderStatusSummaryComponentTitleContainer
            }
          >
            <Typography
              className="PackableOrderStatusSummaryComponentTitle"
              sx={effectiveStyles.PackableOrderStatusSummaryComponentTitle}
              variant={
                effectiveStyles.PackableOrderStatusSummaryComponentTitle
                  ?.muiVariant
              }
            >
              Order Items
            </Typography>
          </Grid>
          <Divider />
          <Grid
            sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}
          >
            <Grid
              sx={{
                display: "flex",
                flexDirection: "column",
                pt: 2,
                //pl: 4,
                pr: 2,
              }}
            >
              <Grid
                sx={{
                  display: "flex", 
                  flexDirection: "row", 
                  justifyContent: "flex-end", 
                }}
              >
                <Typography variant="body1">Storage Shipping</Typography>
              </Grid>
              <Divider />
              <Grid
                className="PackableSystmeInfoAttributesContainer"
                sx={effectiveStyles.PackableSystmeInfoAttributesContainer}
              >
                {Object.values(ShippingStatus).map((value) =>
                  renderShippingStatusCount(
                    getShippingStatusCount(ShipmentType.STORAGE, value)
                  )
                )}
              </Grid>
            </Grid>
            <Grid
              sx={{
                display: "flex",
                flexDirection: "column",
                pt: 2,
                //pl: 4,
                pr: 2,
              }}
            >
              <Grid
                sx={{
                  display: "flex", 
                  flexDirection: "row", 
                  justifyContent: "flex-end", 
                }}
              >
                <Typography variant="body1">Home Shipping</Typography>
              </Grid>
              <Divider />
              <Grid
                className="PackableSystmeInfoAttributesContainer"
                sx={effectiveStyles.PackableSystmeInfoAttributesContainer}
              >
                {Object.values(ShippingStatus)
                  .filter(
                    (value) =>
                      value === ShippingStatus.REQUESTED ||
                      value === ShippingStatus.PICKUP_SCHEDULED ||
                      value === ShippingStatus.PICKUP_COMPLETED ||
                      value === ShippingStatus.PICKUP_REJECTED ||
                      value === ShippingStatus.DELIVERED
                  )
                  .map((value) =>
                    renderShippingStatusCount(
                      getShippingStatusCount(ShipmentType.HOME, value)
                    )
                  )}
              </Grid>
            </Grid>
            <Grid
              sx={{
                display: "flex",
                flexDirection: "column",
                pt: 2,
                //pl: 4,
                pr: 2,
              }}
            >
              <Grid
                sx={{
                  display: "flex", 
                  flexDirection: "row", 
                  justifyContent: "flex-end", 
                }}
              >
                <Typography variant="body1">Storage</Typography>
              </Grid>
              <Divider />
              <Grid
                className="PackableSystmeInfoAttributesContainer"
                sx={effectiveStyles.PackableSystmeInfoAttributesContainer}
              >
                {Object.values(StorageStatus).map((value) =>
                  renderStorageStatusCount(getStorageStatusCount(value))
                )}
              </Grid>
            </Grid>
          </Grid>
      </Grid>
    </div>
  );
};

export default OrderStatusSummaryComponent;
